/**
 * R30 Launch Promotion Banner + Countdown
 * ==========================================
 * Fetches /api/promotions/active (server-authoritative expiry) and renders
 * a prominent, dismissible banner with a live countdown to the promo end
 * time. Auto-hides after `end_at` (both because the endpoint returns
 * active=false AND because the client-side ticker enforces the cut-off).
 *
 * Ships intentionally simple: no confetti, no CSS animations that would burn
 * mobile CPU, no localStorage dismiss (we WANT this in the customer's face
 * for the full 61-day window).
 */
import React, { useEffect, useState } from 'react';
import { Tag } from 'lucide-react';

const API_URL =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) ||
  process.env.REACT_APP_BACKEND_URL ||
  '';

const pad2 = (n) => String(n).padStart(2, '0');

function LaunchPromoBanner() {
  const [promo, setPromo] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/promotions/active`)
      .then((r) => (r.ok ? r.json() : { active: false }))
      .then((j) => {
        if (!cancelled && j && j.active) setPromo(j);
      })
      .catch(() => {});
    // Re-check every 10 min so the banner auto-clears within 10 min of
    // Oct 31 23:59 JM without a page refresh.
    const poll = setInterval(() => {
      fetch(`${API_URL}/api/promotions/active`)
        .then((r) => (r.ok ? r.json() : { active: false }))
        .then((j) => {
          if (!cancelled) setPromo(j && j.active ? j : null);
        })
        .catch(() => {});
    }, 10 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, []);

  useEffect(() => {
    if (!promo) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [promo]);

  if (!promo) return null;

  const endMs = new Date(promo.end_at).getTime();
  const diff = Math.max(0, endMs - now);
  if (diff <= 0) return null;

  const days = Math.floor(diff / (24 * 3600 * 1000));
  const hours = Math.floor((diff % (24 * 3600 * 1000)) / (3600 * 1000));
  const mins = Math.floor((diff % (3600 * 1000)) / (60 * 1000));
  const secs = Math.floor((diff % (60 * 1000)) / 1000);

  const rates = promo.rates || {};

  return (
    <div
      data-testid="launch-promo-banner"
      className="w-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-slate-900 shadow-lg"
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <div className="flex items-center gap-2 min-w-fit">
            <Tag className="w-5 h-5" />
            <span className="font-bold text-sm md:text-base uppercase tracking-wide">
              Liantiao Launch Special
            </span>
          </div>

          <div className="flex-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="font-semibold">Flat-rate deliveries from J$850!</span>
            <span data-testid="promo-rate-kingston" className="whitespace-nowrap">
              Kingston <b>J${rates.kingston_kingston ?? 850}</b>
            </span>
            <span data-testid="promo-rate-portmore" className="whitespace-nowrap">
              Portmore <b>J${rates.portmore_portmore ?? 980}</b>
            </span>
            <span data-testid="promo-rate-spanish-town" className="whitespace-nowrap">
              Spanish Town <b>J${rates.spanish_town_spanish_town ?? 1080}</b>
            </span>
            {rates.pudo_flat != null && (
              <span data-testid="promo-rate-pudo" className="whitespace-nowrap">
                PUDO <b>J${rates.pudo_flat}</b>
              </span>
            )}
          </div>

          <div
            data-testid="promo-countdown"
            className="flex items-center gap-1.5 md:gap-2 font-mono text-sm md:text-base bg-slate-900/10 rounded-md px-2.5 py-1"
          >
            <div className="flex flex-col items-center leading-none">
              <span className="font-bold text-base md:text-lg" data-testid="promo-countdown-days">
                {days}
              </span>
              <span className="text-[10px] opacity-80">DAYS</span>
            </div>
            <span>:</span>
            <div className="flex flex-col items-center leading-none">
              <span className="font-bold text-base md:text-lg" data-testid="promo-countdown-hours">
                {pad2(hours)}
              </span>
              <span className="text-[10px] opacity-80">HRS</span>
            </div>
            <span>:</span>
            <div className="flex flex-col items-center leading-none">
              <span className="font-bold text-base md:text-lg" data-testid="promo-countdown-mins">
                {pad2(mins)}
              </span>
              <span className="text-[10px] opacity-80">MIN</span>
            </div>
            <span className="hidden md:inline">:</span>
            <div className="hidden md:flex flex-col items-center leading-none">
              <span className="font-bold text-base md:text-lg" data-testid="promo-countdown-secs">
                {pad2(secs)}
              </span>
              <span className="text-[10px] opacity-80">SEC</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LaunchPromoBanner;
